import {
    createEditor,
    Transforms,
    type Descendant,
    type Editor,
    type Element,
} from "slate";
import {HistoryEditor, withHistory} from "slate-history";
import {withReact} from "slate-react";

import {withChangeTracking} from "./plugins/with-change-tracking.ts";
import {withCode} from "./plugins/with-code.ts";
import {withHardBreak} from "./plugins/with-hard-break.ts";
import {withHelpers} from "./plugins/with-helpers.ts";
import {withHorizontalRules} from "./plugins/with-horizontal-rules.ts";
import {withImages} from "./plugins/with-images.ts";
import {withLinks} from "./plugins/with-links.ts";
import {withLists} from "./plugins/with-lists.ts";
import {withMath} from "./plugins/with-math.ts";
import {withSkeletons} from "./plugins/with-skeletons.ts";
import {withSmartDelete} from "./plugins/with-smart-delete.ts";
import {withTables} from "./plugins/with-tables.ts";

// Slate needs at least this initial empty state to render our nodes correctly:
const defaultValue: Descendant[] = [
    {
        type: "paragraph",
        children: [{text: ""}],
    },
];

/**
 * Creates a new, empty Slate editor with all the plugins we need to support
 * coediting.
 */
export default function createSlateEditor(
    initialValue?: Array<Element>,
): Editor {
    const editor = composePlugins(
        withReact,
        withHistory,
        withHelpers,
        withChangeTracking,
        withSmartDelete,
        withHardBreak,
        withCode,
        withMath,
        withHorizontalRules,
        withImages,
        withLinks,
        withLists,
        withSkeletons,
        withTables,
    )(createEditor());

    // Populate the editor with some initial value, but don't save this
    // operation to the undo history (we don't want to allow this to be
    // undone).
    HistoryEditor.withoutSaving(editor, () => {
        Transforms.insertNodes(editor, initialValue || defaultValue);
    });

    return editor;
}

const composePlugins = (...plugins: ((editor: Editor) => Editor)[]) => {
    return (editor: Editor) =>
        plugins.reduceRight((e, plug) => plug(e), editor);
};
